// Server functions behind the layouts' session data.
// They run in the Worker during SSR and over RPC on client-side navigations; both act as the visitor
// (src/lib/RequestState.ts forwards their cookies to the backend).
import { createServerFn } from "@tanstack/react-start";
import { COOKIE_CONSENT_COOKIE, parseCookieConsent } from "shared/CookieConsent";
import { getSavedDocsLanguage } from "src/lib/DocsLanguage";
import { getSavedDocsUiLibrary } from "src/lib/DocsUiLibrary";
import { getRequestCookies } from "src/lib/RequestState";
import { fetchLayoutData, fetchRootAuthState, type PortalScopeQuery } from "src/lib/ServerUtils";
import { lookupPlatformState } from "src/server/PlatformStatus";

/**
 * The signed-in user (root layout), or undefined, and the visitor's cookie choice, so the consent
 * banner renders on the server. Cached pages are keyed by consent state (src/lib/CFCache.ts). On
 * portal and docs pages the user comes from the portal view their layout loads (src/lib/ServerUtils.ts).
 */
export const getAuthState = createServerFn({ method: "GET" }).handler(async () => ({
  authState: await fetchRootAuthState(),
  cookieConsent: parseCookieConsent(getRequestCookies()?.get(COOKIE_CONSENT_COOKIE.name)?.value),
}));

/** Session, projects, selected project, its team and the moderation profiles for the portal layout. */
export const getPortalLayoutData = createServerFn({ method: "GET" })
  .validator((scope: PortalScopeQuery) => scope)
  .handler(async ({ data: scope }) => {
    // The platform state renders with the sidebar's status chip; it waits briefly, never for long.
    const [data, platformState] = await Promise.all([
      fetchLayoutData(scope).catch((error) => {
        console.error("Portal layout failed to load auth state or projects", error);
        throw error;
      }),
      lookupPlatformState(400),
    ]);
    return { ...data, platformState };
  });

/**
 * Session, projects, the selected project, its team and the moderation profiles for the docs layout
 * (signed-in visitors keep their project switcher there), and the SDK language docs examples
 * render with: the saved choice, read from the visitor's cookie only with functional-cookie consent
 * (src/lib/DocsLanguage.ts). The project data is the portal layout's, so the portal context makes no
 * requests right after hydration: updating it then, above the still-hydrating article, would make
 * React discard the article's server HTML.
 */
export const getDocsLayoutData = createServerFn({ method: "GET" }).handler(async () => {
  const data = await fetchLayoutData();
  const cookies = getRequestCookies();
  return {
    ...data,
    initialLanguage: getSavedDocsLanguage((name) => cookies?.get(name)?.value),
    initialUiLibrary: getSavedDocsUiLibrary((name) => cookies?.get(name)?.value),
  };
});

/** The SDK language and UI library the blog's examples render with, as getDocsLayoutData's. */
export const getBlogLayoutData = createServerFn({ method: "GET" }).handler(() => {
  const cookies = getRequestCookies();
  return {
    initialLanguage: getSavedDocsLanguage((name) => cookies?.get(name)?.value),
    initialUiLibrary: getSavedDocsUiLibrary((name) => cookies?.get(name)?.value),
  };
});
